import { getMetaLeadFormReadiness } from "./form-readiness"
import type {
  MetaAdsFormHandoff,
  MetaAdsFormScope,
  MetaAdsIntegrationView,
  MetaLeadForm,
} from "./types"

type MetaAdsReadinessFlowInput = {
  formsInScope: MetaLeadForm[]
  handoffs: MetaAdsFormHandoff[]
  phoneFieldKey: string
  requiresPhoneField?: boolean
}

export type MetaAdsReadinessFlow = {
  formsNeedingCopy: MetaLeadForm[]
  remediatedSourceIds: Set<string>
  visibleHandoffs: MetaAdsFormHandoff[]
}

export type MetaAdsPostConnectionStep =
  | "check_forms"
  | "create_copy"
  | "update_ad"
  | "assign_crm"
  | "test_connection"

/** The Meta test-lead form supplies only a phone number, so it cannot test Email or WhatsApp routing. */
export function canSendMetaAdsCallbackTest(integration: MetaAdsIntegrationView): boolean {
  if (!integration.voice_agent_id) return false
  return Object.values(integration.form_action_overrides).every(actions => (
    actions[0]?.actionType === "schedule_call"
  ))
}

export function getMetaAdsReadinessFlow({
  formsInScope,
  handoffs,
  phoneFieldKey,
  requiresPhoneField = true,
}: MetaAdsReadinessFlowInput): MetaAdsReadinessFlow {
  const formIdsInScope = new Set(formsInScope.map(form => form.id))
  const scopedHandoffs = handoffs.filter(handoff => (
    formIdsInScope.has(handoff.sourceForm.id)
    || formIdsInScope.has(handoff.copiedForm.id)
  ))
  const latestHandoffs = getLatestMetaAdsFormHandoffsBySource(scopedHandoffs)
  const remediatedSourceIds = new Set(
    latestHandoffs.map(handoff => handoff.sourceForm.id),
  )
  const formsNeedingCopy = formsInScope.filter(form => {
    const readiness = getMetaLeadFormReadiness(form, phoneFieldKey)
    return !remediatedSourceIds.has(form.id) && (
      readiness.deliveryMode === "targeted_ads_only"
      || (requiresPhoneField && !readiness.configuredPhoneFieldPresent)
    )
  })

  if (formsNeedingCopy.length > 0) {
    return {
      formsNeedingCopy,
      remediatedSourceIds,
      visibleHandoffs: [],
    }
  }

  const pendingHandoffs = latestHandoffs.filter(handoff => !handoff.acknowledgedAt)
  return {
    formsNeedingCopy,
    remediatedSourceIds,
    visibleHandoffs: pendingHandoffs.length > 0
      ? pendingHandoffs
      : latestHandoffs.slice(0, 1),
  }
}

export function getMetaAdsPostConnectionStep({
  hasForms,
  needsCopy,
  hasPendingHandoff,
  crmAccessConfirmed,
}: {
  hasForms: boolean
  needsCopy: boolean
  hasPendingHandoff: boolean
  crmAccessConfirmed: boolean
}): MetaAdsPostConnectionStep {
  if (!hasForms) return "check_forms"
  if (needsCopy) return "create_copy"
  if (hasPendingHandoff) return "update_ad"
  if (!crmAccessConfirmed) return "assign_crm"
  return "test_connection"
}

export function getLatestMetaAdsFormHandoffsBySource(
  handoffs: MetaAdsFormHandoff[],
): MetaAdsFormHandoff[] {
  const latestBySourceId = new Map<string, MetaAdsFormHandoff>()
  for (const handoff of handoffs) {
    const current = latestBySourceId.get(handoff.sourceForm.id)
    if (!current || handoff.createdAt > current.createdAt) {
      latestBySourceId.set(handoff.sourceForm.id, handoff)
    }
  }
  return [...latestBySourceId.values()]
}

export function isMetaAdsFormHandoffInScope({
  handoff,
  formScope,
  selectedFormIds,
}: {
  handoff: MetaAdsFormHandoff
  formScope: MetaAdsFormScope
  selectedFormIds: string[]
}): boolean {
  if (formScope === "all") return true
  const selectedIds = new Set(selectedFormIds)
  return selectedIds.has(handoff.sourceForm.id)
    || selectedIds.has(handoff.copiedForm.id)
}
